import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, ArrowRight, FloppyDisk, CheckCircle, Plus, Trash } from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";
import ContextCarryOver from "@/components/ContextCarryOver";

const STEPS = [
  { n: 1, code: "1", key: "suppliers", letter: "S", label: "Suppliers", phase: "OPERATIONS", placeholder: "Provider of a core input (internal dept or external contractor)" },
  { n: 2, code: "2", key: "inputs", letter: "I", label: "Inputs", phase: "OPERATIONS", placeholder: "Raw material, data or equipment required" },
  { n: 3, code: "3", key: "process", letter: "P", label: "Process", phase: "OPERATIONS", placeholder: "High-level macro step (keep to 5–7)" },
  { n: 4, code: "4", key: "outputs", letter: "O", label: "Outputs", phase: "OPERATIONS", placeholder: "Final deliverable, data package or completed asset" },
  { n: 5, code: "5", key: "customers", letter: "C", label: "Customers", phase: "OPERATIONS", placeholder: "Immediate recipient or downstream user of an output" },
];
const LAST = STEPS.length;
const empty = { suppliers: [""], inputs: [""], process: [""], outputs: [""], customers: [""] };
const inputCls = "flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";

export default function Sipoc() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [data, setData] = useState(empty);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/documents/${id}`);
        setTitle(res.data.title || "");
        const p = res.data.payload || {};
        setData({
          suppliers: p.suppliers?.length ? p.suppliers : [""],
          inputs: p.inputs?.length ? p.inputs : [""],
          process: p.process?.length ? p.process : [""],
          outputs: p.outputs?.length ? p.outputs : [""],
          customers: p.customers?.length ? p.customers : [""],
        });
      } catch { toast.error("Document not found"); navigate("/dashboard"); }
      finally { setLoading(false); }
    })();
  }, [id, navigate]);

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try { await api.put(`/documents/${id}`, { title: title || "Untitled SIPOC Map", payload: data, ...extra }); return true; }
    catch { toast.error("Save failed"); return false; }
    finally { setSaving(false); }
  }, [id, title, data]);

  const cur = STEPS[step - 1];
  const list = data[cur.key];
  const setItem = (i, v) => setData((d) => ({ ...d, [cur.key]: d[cur.key].map((x, j) => (j === i ? v : x)) }));
  const addItem = () => setData((d) => ({ ...d, [cur.key]: [...d[cur.key], ""] }));
  const removeItem = (i) => setData((d) => ({ ...d, [cur.key]: d[cur.key].filter((_, j) => j !== i) }));

  const saveDraft = async () => { if (await persist()) toast.success("Draft saved"); };
  const next = async () => { await persist(); setStep((s) => Math.min(LAST, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const finalise = async () => { if (await persist({ status: "Completed" })) { toast.success("SIPOC map finalised"); navigate(`/sipoc/${id}/view`); } };
  const applySuggestion = (text) => { setData((d) => ({ ...d, [cur.key]: [...d[cur.key].filter((x) => x.trim()), text] })); toast.success("Added to this step"); };
  const onPull = ({ title: t, context }) => { if (!title && t) setTitle(t); if (context) setData((d) => ({ ...d, suppliers: [...d.suppliers.filter((x) => x.trim()), context] })); toast.success("Context pulled in"); };

  if (loading) return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft size={16} /> Workspace</button>
        <input data-testid="sipoc-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="SIPOC map title…"
          className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1" />

        <div className="mt-5 grid grid-cols-5 gap-2" data-testid="sipoc-stepper">
          {STEPS.map((s) => (
            <button key={s.n} data-testid={`step-${s.n}`} onClick={() => setStep(s.n)}
              className={`rounded-sm border px-2 py-2.5 text-center transition-all ${step === s.n ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className={`font-mono text-base font-bold block ${step === s.n ? "text-blue-700" : "text-slate-400"}`}>{s.letter}</span>
              <p className={`text-[11px] font-medium ${step === s.n ? "text-slate-900" : "text-slate-500"}`}>{s.label}</p>
            </button>
          ))}
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-slate-900">{cur.letter} — {cur.label}</h2>
            <button data-testid="sipoc-add-item" onClick={addItem} className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"><Plus size={16} /> Add row</button>
          </div>
          {step === 1 && <ContextCarryOver currentId={id} onPull={onPull} />}
          <div className="space-y-2">
            {list.map((v, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="font-mono text-xs text-slate-400 w-5">{i + 1}.</span>
                <input data-testid={`sipoc-${cur.key}-${i}`} value={v} onChange={(e) => setItem(i, e.target.value)} placeholder={cur.placeholder} className={inputCls + " flex-1"} />
                <button data-testid={`sipoc-remove-${cur.key}-${i}`} onClick={() => removeItem(i)} className="p-2 text-slate-400 hover:text-red-600"><Trash size={16} /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="sipoc-back" onClick={back} disabled={step === 1} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"><ArrowLeft size={16} /> Back</button>
          <div className="flex items-center gap-3">
            <button data-testid="sipoc-save-draft" onClick={saveDraft} disabled={saving} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">{saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft</button>
            {step < LAST ? (
              <button data-testid="sipoc-next" onClick={next} className="inline-flex items-center gap-2 rounded-sm bg-blue-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-800">Next Step <ArrowRight size={16} /></button>
            ) : (
              <button data-testid="sipoc-finalise" onClick={finalise} disabled={saving} className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700"><CheckCircle size={18} weight="fill" /> Finalise Map</button>
            )}
          </div>
        </div>
      </div>

      <CoPilotToggle open={open} onClick={() => setOpen(true)} phase={cur.phase} />
      <CoPilotPanel open={open} onClose={() => setOpen(false)} docId={id} toolType="sipoc" step={cur} phase={cur.phase} onApplySuggestion={applySuggestion} />
    </Layout>
  );
}
